import type { Emotion } from '../types/emotion';

/**
 * Hero-only desktop play behaviours for Pixel:
 *
 *  interest     Click fast, repeatedly, in one spot → he notices, cocks his head and stares at it.
 *  investigate  Keep clicking → he flies over, circles the spot, sniffs at it with scanning eyes,
 *               startles at every new click.
 *  focus        Cursor close around his face → eyes widen, pupils lock onto the cursor.
 *  dizzy        Keep circling his face → spiral eyes, wobbling.
 *  hypno        Keep circling → full hypnosis for 20s: crazy spinning spiral eyes, he follows your
 *               cursor everywhere in the hero and shoves UI elements out of his way.
 *
 * Everything resets once you scroll deep into the showreel.
 */

export type PlayMode = 'none' | 'focus' | 'dizzy' | 'interest' | 'investigate' | 'board' | 'hypno';

export interface EyeFx {
  /** 0..1 eyes wide open with a pupil locked on the cursor */
  wide: number;
  /** 0..1 spiral eyes */
  spiral: number;
  /** 0..1 tighter, pulsing spiral (hypnosis) */
  tight: number;
  /** spiral rotation phase (rad) */
  spin: number;
  /** 0..1 scanning bar across the eyes */
  scan: number;
}

export interface PlayOffsets {
  x: number;
  y: number;
  z: number;
  pitch: number;
  yaw: number;
  roll: number;
  squint: number;
  led: number;
  /** 0..1 flat on the "table" (seen from above) */
  board: number;
  wide: number;
  spiral: number;
  tight: number;
  scan: number;
}

export const PLAY_OFFSET_KEYS: Array<keyof PlayOffsets> = [
  'x', 'y', 'z', 'pitch', 'yaw', 'roll', 'squint', 'led', 'board', 'wide', 'spiral', 'tight', 'scan',
];

export interface PlayPose extends PlayOffsets {
  mode: PlayMode;
  /** Rotation around the view axis while on the board (rad) */
  heading: number;
  spin: number;
  arms: number | null;
  emotion: Emotion | null;
  gaze: { x: number; y: number } | null;
  headFollow: number;
  /** How fast the scene eases toward this pose */
  rate: number;
  /** Shove UI elements out of the way */
  push: boolean;
}

export interface PlayFrame {
  t: number;
  dt: number;
  enabled: boolean;
  /** Cursor (px) */
  pointer: { x: number; y: number };
  pointerInWindow: boolean;
  /** Face display centre + radius (px), or null if not visible */
  face: { x: number; y: number; r: number } | null;
  /** Drone centre (px) and radius (px) */
  drone: { x: number; y: number };
  droneR: number;
  /** Where the drone rests (world, z = 0) */
  home: { x: number; y: number };
  /** Half view size (world) at z = 0 */
  view: { w: number; h: number };
  width: number;
  height: number;
  /** Hero bounds on screen (px) */
  heroTop: number;
  heroBottom: number;
}

const DIZZY_TURNS = 1.4;
const HYPNO_TURNS = 4;
export const HYPNO_DUR = 20;
const INTEREST_AT = 5;
const INVESTIGATE_AT = 11;
const INVESTIGATE_LINGER = 5.5;
const CAM = 6.5;

const clamp = (v: number, a: number, b: number) => Math.min(b, Math.max(a, v));
const clamp01 = (v: number) => clamp(v, 0, 1);
const smooth = (v: number) => {
  const c = clamp01(v);
  return c * c * (3 - 2 * c);
};
const wrap = (a: number) => Math.atan2(Math.sin(a), Math.cos(a));

export class HeroPlay {
  private down = false;
  private lastPtr = { x: 0, y: 0 };
  private ptrSpeed = 0;

  // Rapid clicks → interest / investigate
  private meter = 0;
  private lastClickAt = -10;
  private clickPos = { x: 0, y: 0 };
  private interest = false;
  private investigateAt = -1;
  private kickAt = -10;

  // Circling the face → focus / dizzy / hypno
  private turns = 0;
  private dir = 0;
  private lastAngle: number | null = null;
  private focus = 0;
  private hypnoAt = -1;

  private spin = 0;
  private enabledNow = false;

  /** Is any behaviour running (so the scene can stop roaming etc.) */
  active = false;

  pointerDown(px: number, py: number, t: number, onDrone: boolean, interactive: boolean) {
    if (!this.enabledNow || interactive || onDrone) return;
    this.down = true;
    // Rapid clicks in one spot build up interest
    const far = Math.hypot(px - this.clickPos.x, py - this.clickPos.y) > 220;
    if (far && this.investigateAt < 0) this.meter = 0;
    this.meter = Math.min(this.meter + 1, 22);
    this.lastClickAt = t;
    this.clickPos = { x: px, y: py };
    if (this.investigateAt >= 0) this.kickAt = t;
  }

  pointerUp() {
    this.down = false;
  }

  /** Is the mouse held down in the hero (used to suppress text selection) */
  get holding() {
    return this.down;
  }

  reset() {
    this.down = false;
    this.meter = 0;
    this.interest = false;
    this.investigateAt = -1;
    this.turns = 0;
    this.dir = 0;
    this.lastAngle = null;
    this.focus = 0;
    this.hypnoAt = -1;
    this.active = false;
  }

  update(f: PlayFrame): PlayPose | null {
    const { t, dt } = f;
    this.enabledNow = f.enabled;
    if (!f.enabled) {
      this.reset();
      return null;
    }

    // Cursor speed (px/s), smoothed
    const raw = Math.hypot(f.pointer.x - this.lastPtr.x, f.pointer.y - this.lastPtr.y) / Math.max(dt, 1e-3);
    this.ptrSpeed += (raw - this.ptrSpeed) * (1 - Math.exp(-dt * 12));
    this.lastPtr = { ...f.pointer };

    const toWorld = (px: number, py: number) => ({
      x: ((px / f.width) * 2 - 1) * f.view.w,
      y: -((py / f.height) * 2 - 1) * f.view.h,
    });
    const pxToWorld = (d: number) => (d / (f.height / 2)) * f.view.h;
    /** Keep a screen point inside the hero (the reel below is a hard border) */
    const inHero = (px: number, py: number, margin: number) => ({
      x: clamp(px, margin, f.width - margin),
      y: clamp(py, Math.max(f.heroTop, 0) + margin, Math.max(f.heroTop + margin * 2, f.heroBottom - margin)),
    });

    // ---- Hypnosis timer ----
    if (this.hypnoAt >= 0 && t - this.hypnoAt > HYPNO_DUR) {
      this.hypnoAt = -1;
      this.turns = 0;
      this.dir = 0;
    }
    const hypno = this.hypnoAt >= 0;

    // ---- Rapid clicks ----
    if (t - this.lastClickAt > 0.35) this.meter = Math.max(0, this.meter - dt * 1.8);
    if (!hypno) {
      if (this.investigateAt < 0 && this.meter >= INVESTIGATE_AT) this.investigateAt = t;
      if (!this.interest && this.meter >= INTEREST_AT) this.interest = true;
      if (this.interest && this.meter < 1 && t - this.lastClickAt > 1.5) this.interest = false;
      if (this.investigateAt >= 0 && t - this.lastClickAt > INVESTIGATE_LINGER) {
        this.investigateAt = -1;
        this.interest = false;
        this.meter = 0;
      }
    }
    const investigating = this.investigateAt >= 0 && !hypno;
    const interested = this.interest && !investigating && !hypno;

    // ---- Circling the face ----
    let inRing = false;
    if (!hypno && !investigating && f.face && f.pointerInWindow) {
      const dx = f.pointer.x - f.face.x;
      const dy = f.pointer.y - f.face.y;
      const dist = Math.hypot(dx, dy);
      inRing = dist > f.face.r * 0.3 && dist < f.face.r * 2.4;
      if (inRing) {
        const a = Math.atan2(dy, dx);
        if (this.lastAngle !== null) {
          const d = wrap(a - this.lastAngle);
          const step = Math.abs(d) / (Math.PI * 2);
          if (this.dir === 0 || Math.sign(d) === this.dir) {
            this.turns += step;
            if (Math.abs(d) > 0.002) this.dir = Math.sign(d);
          } else {
            this.turns -= step * 0.6;
            if (this.turns < 0.1) this.dir = Math.sign(d);
          }
        }
        this.lastAngle = a;
      } else {
        this.lastAngle = null;
        this.turns -= dt * 0.8;
      }
      this.turns -= dt * 0.1;
      this.turns = Math.max(0, this.turns);
      if (this.turns >= HYPNO_TURNS) {
        this.hypnoAt = t;
        this.turns = 0;
      }
    } else if (!hypno) {
      this.lastAngle = null;
      this.turns = Math.max(0, this.turns - dt * 0.8);
    }
    this.focus += ((inRing ? 1 : 0) - this.focus) * (1 - Math.exp(-dt * 6));

    // Spiral rotation phase
    const spinRate = hypno ? 9 + 3 * Math.sin(t * 2.3) : 3.2 + this.turns * 1.2;
    this.spin += dt * spinRate;

    const pose: PlayPose = {
      mode: 'none',
      x: 0, y: 0, z: 0, pitch: 0, yaw: 0, roll: 0, squint: 0, led: 0, board: 0,
      wide: 0, spiral: 0, tight: 0, scan: 0,
      heading: 0, spin: this.spin, arms: null, emotion: null, gaze: null, headFollow: 0.55, rate: 4, push: false,
    };
    /** Direction from the drone to a screen point, in the eyes' -1..1 range */
    const lookToward = (px: number, py: number, reach: number) => ({
      x: clamp((px - f.drone.x) / reach, -1, 1),
      y: clamp(-(py - f.drone.y) / reach, -1, 1),
    });

    // ===== HYPNO =====
    if (hypno) {
      const ht = t - this.hypnoAt;
      const fade = smooth(ht / 0.6) * smooth((HYPNO_DUR - ht) / 1.4);
      const target = inHero(f.pointer.x, f.pointer.y, f.droneR * 0.9);
      const w = toWorld(target.x, target.y);
      const z = 0.25 * fade;
      const k = (CAM - z) / CAM;
      pose.mode = 'hypno';
      pose.x = (w.x * k - f.home.x) * fade + Math.sin(t * 2.7) * 0.06 * fade;
      pose.y = (w.y * k - f.home.y) * fade + Math.sin(t * 8.5) * 0.05 * fade;
      pose.z = z;
      pose.roll = Math.sin(t * 6.5) * 0.22 * fade;
      pose.yaw = Math.sin(t * 4.1) * 0.28 * fade;
      pose.pitch = Math.sin(t * 5.3) * 0.12 * fade;
      pose.spiral = fade;
      pose.tight = fade;
      pose.led = (1.2 + Math.sin(t * 10) * 0.8) * fade;
      pose.arms = 0.5 + 0.5 * Math.sin(t * 7);
      pose.emotion = 'surprised';
      pose.gaze = { x: Math.sin(t * 3) * 0.3, y: Math.cos(t * 2.6) * 0.2 };
      pose.headFollow = 0.2;
      pose.rate = 5;
      pose.push = fade > 0.4;
      this.active = true;
      return pose;
    }

    // ===== INVESTIGATE =====
    if (investigating) {
      const it = t - this.investigateAt;
      const rWorld = pxToWorld(f.droneR);
      const spot = inHero(this.clickPos.x, this.clickPos.y, f.droneR * 0.6);
      const pw = toWorld(spot.x, spot.y);
      const side = Math.sign(f.home.x - pw.x) || 1;
      // Approach from his side, then slowly circle the spot
      const approach = smooth(it / 1.4);
      const orbit = Math.max(0, it - 1.4) * 0.8;
      const ang = (side > 0 ? 0 : Math.PI) + orbit;
      const R = rWorld * 1.35;
      let txp = f.width * ((pw.x + Math.cos(ang) * R) / f.view.w + 1) / 2;
      let typ = f.height * (1 - (pw.y + Math.sin(ang) * R * 0.6) / f.view.h) / 2;
      const clamped = inHero(txp, typ, f.droneR * 0.9);
      txp = clamped.x;
      typ = clamped.y;
      const target = toWorld(txp, typ);
      const kick = Math.exp(-(t - this.kickAt) * 6);
      const z = 0.3 + kick * 0.35;
      const k = (CAM - z) / CAM;
      // Sniffing nods toward the spot every ~1.8s
      const nodPhase = (it % 1.8) / 1.8;
      const nod = nodPhase > 0.7 ? Math.sin(((nodPhase - 0.7) / 0.3) * Math.PI) : 0;
      const g = lookToward(spot.x, spot.y, f.droneR * 2.2);
      pose.mode = 'investigate';
      pose.x = (target.x * k - f.home.x) * approach;
      pose.y = (target.y * k - f.home.y) * approach + Math.sin(t * 3.1) * 0.03;
      pose.z = z;
      pose.pitch = -g.y * 0.25 * nod + 0.06;
      pose.roll = Math.sin(t * 1.3) * 0.18 - g.x * 0.08;
      pose.gaze = g;
      pose.headFollow = 0.7;
      pose.scan = t - this.kickAt < 0.6 ? 0 : 1;
      pose.wide = 0.5;
      pose.led = 0.4 + kick * 1.2;
      pose.arms = 0.15 + nod * 0.4;
      pose.emotion = t - this.kickAt < 0.6 ? 'surprised' : it % 6 > 4.4 ? 'happy' : 'curious';
      pose.rate = 4;
      this.active = true;
      return pose;
    }

    // ===== INTEREST =====
    if (interested) {
      const g = lookToward(this.clickPos.x, this.clickPos.y, Math.max(f.width, f.height) * 0.3);
      const pw = toWorld(this.clickPos.x, this.clickPos.y);
      pose.mode = 'interest';
      pose.x = (pw.x - f.home.x) * 0.1;
      pose.y = (pw.y - f.home.y) * 0.08;
      pose.z = 0.15;
      pose.roll = 0.2 * (g.x >= 0 ? -1 : 1) + Math.sin(t * 1.6) * 0.04;
      pose.gaze = g;
      pose.headFollow = 0.8;
      pose.wide = 0.65;
      pose.led = 0.3;
      pose.emotion = 'curious';
      pose.rate = 3.5;
      this.active = true;
      return pose;
    }

    // ===== FOCUS / DIZZY =====
    if (this.focus > 0.02 || this.turns > 0.05) {
      const dizzy = smooth((this.turns - DIZZY_TURNS) / 1.2);
      const build = clamp01(this.turns / DIZZY_TURNS);
      const g = f.face
        ? {
            x: clamp((f.pointer.x - f.face.x) / (f.face.r * 2.4), -1, 1),
            y: clamp(-(f.pointer.y - f.face.y) / (f.face.r * 2.4), -1, 1),
          }
        : { x: 0, y: 0 };
      pose.mode = dizzy > 0.05 ? 'dizzy' : 'focus';
      pose.wide = this.focus * (0.55 + 0.45 * build) * (1 - dizzy);
      pose.spiral = dizzy;
      pose.z = 0.15 * this.focus;
      pose.roll = Math.sin(t * 5) * 0.16 * dizzy;
      pose.yaw = Math.sin(t * 3.3) * 0.12 * dizzy;
      pose.x = Math.sin(t * 2.4) * 0.07 * dizzy;
      pose.y = Math.cos(t * 2.1) * 0.05 * dizzy;
      pose.gaze = g;
      pose.headFollow = 0.35;
      pose.emotion = dizzy > 0.3 ? 'surprised' : build > 0.4 ? 'surprised' : 'curious';
      pose.led = 0.2 * this.focus + dizzy * 0.4;
      pose.rate = 5;
      this.active = true;
      return pose;
    }

    this.active = false;
    return null;
  }
}

export const heroPlay = new HeroPlay();
